import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Phone, Mail, HeartHandshake, Users, CheckCircle2, Clock, AlertCircle, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const KINDS = [
  { key: 'call',       label: 'Call',        icon: Phone },
  { key: 'email',      label: 'Email',       icon: Mail },
  { key: 'thank_you',  label: 'Thank-you',   icon: HeartHandshake },
  { key: 'meeting',    label: 'Meeting',     icon: Users },
  { key: 'ask',        label: 'Ask',         icon: AlertCircle },
  { key: 'letter',     label: 'Letter',      icon: Mail },
  { key: 'follow_up',  label: 'Follow-up',   icon: Clock },
  { key: 'other',      label: 'Other',       icon: Plus },
];

/**
 * ActionsTab — task queue with buckets (Today / Overdue / Upcoming / Completed),
 * quick-complete via checkbox, bulk-complete, and add-action dialog per donor.
 */
export function ActionsTab({ api, donors, onChange }) {
  const [tab, setTab] = useState('today');
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/donor-actions?state=${tab}`);
      setActions(data);
    } catch { toast.error('Failed to load actions'); }
    setLoading(false);
  }, [api, tab]);

  useEffect(() => { load(); setSelected(new Set()); }, [load]);

  const complete = async (id) => {
    try {
      await api.put(`/donor-actions/${id}`, { completed: true });
      toast.success('Marked done');
      load(); onChange && onChange();
    } catch { toast.error('Failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try { await api.delete(`/donor-actions/${id}`); load(); }
    catch { toast.error('Failed'); }
  };
  const toggleSel = (id) => setSelected(prev => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const bulkComplete = async () => {
    if (selected.size === 0) return;
    try {
      const res = await api.post('/donor-action-bulk-complete', { action_ids: [...selected] });
      toast.success(`Completed ${res.data.completed}`);
      setSelected(new Set()); load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="actions-tab">
      <div className="flex items-center gap-2 border-b mb-3 flex-wrap">
        {['today', 'overdue', 'upcoming', 'open', 'completed'].map(t => (
          <button key={t} onClick={() => setTab(t)}
                  className={`px-3 py-1.5 text-sm capitalize border-b-2 transition-colors ${tab === t ? 'border-[#6B3FA0] text-[#6B3FA0] font-semibold' : 'border-transparent text-gray-600'}`}
                  data-testid={`actions-filter-${t}`}>
            {t === 'open' ? 'All open' : t}
          </button>
        ))}
        <Button size="sm" onClick={() => setAddOpen(true)} className="ml-auto" data-testid="actions-add-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> New task
        </Button>
      </div>

      {selected.size > 0 && (
        <div className="sticky top-14 z-20 bg-white border-2 border-[#6B3FA0] rounded-lg shadow p-3 mb-3 flex items-center gap-2">
          <span className="text-sm font-semibold text-[#6B3FA0]">{selected.size} selected</span>
          <Button size="sm" onClick={bulkComplete} className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="actions-bulk-complete">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Complete all
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}><X className="h-4 w-4" /></Button>
        </div>
      )}

      {loading ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#6B3FA0]" /></div>
        : actions.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-gray-500">
            {tab === 'today' ? 'Nothing due today.' : tab === 'overdue' ? 'No overdue tasks.' : `No ${tab} tasks yet.`}
          </CardContent></Card>
        ) : (
          <div className="grid gap-1.5">
            {actions.map(a => {
              const kind = KINDS.find(k => k.key === a.kind) || KINDS[7];
              const Icon = kind.icon;
              const isDone = !!a.completed_at;
              return (
                <Card key={a.id} className={`hover:shadow-md ${selected.has(a.id) ? 'ring-2 ring-[#6B3FA0]' : ''}`}
                      data-testid={`action-row-${a.id}`}>
                  <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                    {!isDone && (
                      <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleSel(a.id)}
                             className="shrink-0 cursor-pointer" data-testid={`action-select-${a.id}`} />
                    )}
                    <Icon className={`h-4 w-4 shrink-0 ${isDone ? 'text-gray-400' : 'text-[#6B3FA0]'}`} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${isDone ? 'line-through text-gray-400' : ''}`}>
                        <span className="font-medium cursor-pointer hover:underline"
                              onClick={() => navigate(`/donors/${a.donor_id}`)}>{a.donor_name}</span>
                        <span className="text-gray-500"> · {kind.label}</span>
                      </div>
                      <div className={`text-sm ${isDone ? 'line-through text-gray-400' : 'text-gray-700'}`}>{a.description}</div>
                    </div>
                    {a.due_date && (
                      <Badge variant="outline" className={`text-xs ${a.due_date < new Date().toISOString().slice(0, 10) && !isDone ? 'text-red-600 border-red-300' : ''}`}>
                        {a.due_date}
                      </Badge>
                    )}
                    {!isDone && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => complete(a.id)}
                                data-testid={`action-complete-${a.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(a.id)}
                                data-testid={`action-delete-${a.id}`} className="text-red-500">
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      <AddActionDialog open={addOpen} onClose={() => setAddOpen(false)} api={api} donors={donors} onDone={load} />
    </div>
  );
}

function AddActionDialog({ open, onClose, api, donors, onDone }) {
  const [form, setForm] = useState({ donor_id: '', kind: 'follow_up', description: '', due_date: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [donorQ, setDonorQ] = useState('');

  React.useEffect(() => {
    if (open) setForm({ donor_id: '', kind: 'follow_up', description: '', due_date: '', notes: '' });
  }, [open]);
  if (!open) return null;

  const filteredDonors = donors.filter(d =>
    !donorQ || d.name.toLowerCase().includes(donorQ.toLowerCase()) || (d.email || '').toLowerCase().includes(donorQ.toLowerCase())
  ).slice(0, 8);

  const save = async () => {
    if (!form.donor_id) { toast.error('Pick a donor'); return; }
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    setBusy(true);
    try {
      await api.post('/donor-actions', form);
      toast.success('Task created');
      onDone(); onClose();
    } catch { toast.error('Save failed'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="add-action-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>New task</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Donor</Label>
            {form.donor_id ? (
              <div className="flex items-center gap-2 border rounded px-2 py-1.5 text-sm">
                <span className="flex-1">{donors.find(d => d.id === form.donor_id)?.name}</span>
                <Button variant="ghost" size="sm" onClick={() => setForm(f => ({ ...f, donor_id: '' }))}><X className="h-3.5 w-3.5" /></Button>
              </div>
            ) : (
              <>
                <Input value={donorQ} onChange={e => setDonorQ(e.target.value)} placeholder="Search donor…"
                       data-testid="add-action-donor-search" />
                {donorQ && (
                  <div className="border rounded mt-1 max-h-40 overflow-y-auto">
                    {filteredDonors.map(d => (
                      <button key={d.id} type="button"
                              onClick={() => { setForm(f => ({ ...f, donor_id: d.id })); setDonorQ(''); }}
                              className="w-full text-left px-2 py-1.5 hover:bg-gray-100 text-sm"
                              data-testid={`add-action-donor-${d.id}`}>
                        {d.name} <span className="text-xs text-gray-500">{d.email}</span>
                      </button>
                    ))}
                    {filteredDonors.length === 0 && <div className="p-2 text-sm text-gray-500">No match</div>}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <Label>Kind</Label>
            <select value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))}
                    className="border rounded px-2 py-1.5 w-full text-sm" data-testid="add-action-kind">
              {KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
            </select>
          </div>
          <div>
            <Label>Description</Label>
            <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                   placeholder="e.g., Send handwritten thank-you card" data-testid="add-action-description" />
          </div>
          <div>
            <Label>Due date</Label>
            <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                   data-testid="add-action-due" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      rows={2} data-testid="add-action-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="add-action-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create task'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
